import { IconButton } from '@fluentui/react';
import React, { useContext, useEffect, useState } from 'react';
import { Build } from '../../../models/Build';
import { ThemeContext } from '../../../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../../../Common/util/localStorageUtils';
import { useIsMobile } from '../../../../Common/hooks/useIsMobile';
import { CloudEnvironment } from '../../../../Common/models/CloudEnvironment';
import { CloudVaultCard, parseCloudVaultArtifact } from './CloudVaultCard';

/** Build metadata key holding the decoded CloudVault artifact metadata. */
const cloudVaultMetadataKey = 'cloudVaultMetadata';

interface IBuildMetadataProps {
    item: Build;
    /** The single environment the parent stage targets. */
    environment?: CloudEnvironment;
}

export const BuildMetadata: React.FC<IBuildMetadataProps> = (
    props: IBuildMetadataProps
) => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);
    const isMobile = useIsMobile();

    const metadataFieldNameStyles: React.CSSProperties = {
        flex: '0 0 140px',
        minWidth: 0,
        paddingRight: '5px',
        boxSizing: 'border-box',
    };

    const metadataValueStyles: React.CSSProperties = {
        display: 'flex',
        flex: '1 1 0',
        minWidth: 0,
        border: '1px gray solid',
        paddingLeft: '5px',
        alignItems: 'center',
        justifyContent: 'space-between',
        overflow: 'hidden',
        boxSizing: 'border-box',
    };

    // Long values (UNC build paths) have no spaces to break on, so wrap them explicitly
    // rather than letting the row overflow.
    const desktopMetadataValueStyles: React.CSSProperties = {
        whiteSpace: 'normal',
        wordBreak: 'break-all',
    };

    const metadataContainerStyles: React.CSSProperties = {
        display: 'flex',
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        paddingLeft: '16px',
        paddingBottom: '5px',
        paddingTop: '10px',
        alignItems: 'center',
    };

    const mobileMetadataContainerStyles: React.CSSProperties = {
        padding: '6px 0px',
    };

    const mobileMetadataValueStyles: React.CSSProperties = {
        whiteSpace: 'normal',
        wordBreak: 'break-all',
        display: 'inline-block',
    };

    const mobileMetadataValueContainerStyles: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        overflow: 'hidden',
        border: '1px gray solid',
        justifyContent: 'space-between',
        margin: '6px 0px',
        padding: '6px',
    };

    const copyToClipboard = (value: string) => {
        navigator.clipboard.writeText(value);
    };

    // The CloudVault metadata is json and gets its own card rather than a plain row.
    const cloudVaultValue = props.item.buildMetadataItems.get(
        cloudVaultMetadataKey
    )?.value;

    const cloudVaultArtifact = cloudVaultValue
        ? parseCloudVaultArtifact(cloudVaultValue)
        : undefined;

    const rowItems = Array.from(props.item.buildMetadataItems.entries())
        .filter(
            ([key]) => !(cloudVaultArtifact && key === cloudVaultMetadataKey)
        )
        .map(([, metadata]) => metadata);

    return props.item.showMetadata ? (
        <div
            style={
                isMobile
                    ? {}
                    : {
                          backgroundColor: theme.palette.neutralLighter,
                          width: '100%',
                          maxWidth: '100%',
                          overflow: 'hidden',
                          boxSizing: 'border-box',
                          paddingLeft: '40px',
                      }
            }
        >
            {rowItems.map((metadata) => (
                <div
                    key={metadata.fieldName}
                    style={
                        isMobile
                            ? mobileMetadataContainerStyles
                            : metadataContainerStyles
                    }
                >
                    <strong style={metadataFieldNameStyles}>
                        {metadata.fieldName}
                    </strong>
                    <div
                        style={
                            isMobile
                                ? mobileMetadataValueContainerStyles
                                : metadataValueStyles
                        }
                    >
                        <div
                            style={
                                isMobile
                                    ? mobileMetadataValueStyles
                                    : desktopMetadataValueStyles
                            }
                        >
                            {metadata.value}
                        </div>
                        <IconButton
                            iconProps={{ iconName: 'Copy' }}
                            onClick={() => copyToClipboard(metadata.value)}
                            title={'Copy to clipboard'}
                            ariaLabel={`Copy ${
                                metadata.fieldName ?? 'value'
                            } to clipboard`}
                        />
                    </div>
                </div>
            ))}
            {cloudVaultArtifact && (
                <div
                    style={
                        isMobile
                            ? mobileMetadataContainerStyles
                            : metadataContainerStyles
                    }
                >
                    {/* Empty label cell so the card lines up with the value boxes above. */}
                    {!isMobile && <div style={metadataFieldNameStyles} />}
                    <div
                        style={{
                            width: isMobile ? '100%' : undefined,
                            flex: isMobile ? undefined : '1 1 0',
                            minWidth: 0,
                        }}
                    >
                        <CloudVaultCard
                            artifact={cloudVaultArtifact}
                            environment={props.environment}
                            uploadedArtifactId={props.item.cloudVaultArtifactId}
                            uploadedOn={props.item.cloudVaultUploadedOn}
                        />
                    </div>
                </div>
            )}
        </div>
    ) : (
        <div></div>
    );
};
